import React from 'react';
import styled from 'styled-components';
import { usePrivy, useLogin } from '@privy-io/react-auth';

const WalletConnectionContainer = styled.div`
  height: 100vh;
  width: 100%;
  background: #000000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  overflow-y: auto;
  position: relative;
  padding: 40px 20px 40px 20px;
  box-sizing: border-box;
  
  /* Desktop PWA constraint */
  @media (min-width: 768px) {
    max-width: 414px;
    width: 414px;
    margin: 0 auto;
    box-shadow: 0 0 40px rgba(0, 0, 0, 0.8), 
                0 0 0 12px rgba(0, 0, 0, 0.5),
                0 0 0 16px #333333;
    border-radius: 24px;
    left: 50%;
    transform: translateX(-50%);
    position: relative;
    z-index: 1;
  }
`;

const LogoImage = styled.img`
  max-width: 250px;
  width: 100%;
  height: auto;
  object-fit: contain;
  margin-top: 40px;
`;

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 100%;
  max-width: 300px;
  margin-bottom: 40px;
`;

const PrimaryButton = styled.button`
  width: 100%;
  max-width: 280px;
  height: 52px;
  font-family: 'Plus Jakarta Sans', sans-serif;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border: none;
  border-radius: 12px;
  color: #ffffff;
  cursor: pointer;
  font-size: 16px;
  font-weight: 600;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
  
  &:hover {
    background: linear-gradient(135deg, #34d399 0%, #10b981 100%);
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(16, 185, 129, 0.4);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: wait;
  }
`;

const SecondaryButton = styled.button`
  width: 100%;
  max-width: 280px;
  height: 52px;
  font-family: 'Plus Jakarta Sans', sans-serif;
  background: transparent;
  border: 1px solid #333333;
  border-radius: 12px;
  color: #ffffff;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: #444444;
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const SkipLink = styled.a`
  margin-top: 24px;
  margin-bottom: 20px;
  color: rgba(255, 255, 255, 0.5);
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 14px;
  text-decoration: none;
  cursor: pointer;
  transition: color 0.2s ease;
  
  &:hover {
    color: rgba(255, 255, 255, 0.8);
  }
`;

const BottomSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const Tagline = styled.p`
  color: #71717a;
  font-size: 14px;
  text-align: center;
  margin-bottom: 32px;
  font-family: 'Plus Jakarta Sans', sans-serif;
  max-width: 280px;
`;

interface WalletConnectionScreenProps {
  onProceed: () => void;
}

const WalletConnectionScreen: React.FC<WalletConnectionScreenProps> = ({ onProceed }) => {
  const { ready, authenticated } = usePrivy();
  
  const { login } = useLogin({
    onComplete: () => {
      console.log('✅ Privy login complete - proceeding to app');
      onProceed();
    },
    onError: (error) => {
      console.error('❌ Privy login error:', error);
    },
  });

  // Auto-proceed if already authenticated
  React.useEffect(() => {
    if (authenticated) {
      onProceed();
    }
  }, [authenticated, onProceed]);

  const handleSignIn = () => {
    if (!ready) return;
    login();
  };

  const handleSkip = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    onProceed();
  };

  return (
    <WalletConnectionContainer>
      <LogoImage 
        src="/landing-assets/mavrk_word_logo.png" 
        alt="Mavrk" 
      />
      <BottomSection>
        <Tagline>
          Trade tokens on Ink Chain with embedded wallets or connect your own
        </Tagline>
        <ButtonsContainer>
          <PrimaryButton onClick={handleSignIn} disabled={!ready}>
            {ready ? 'Sign In' : 'Loading...'}
          </PrimaryButton>
          <SecondaryButton onClick={handleSignIn} disabled={!ready}>
            Connect Wallet
          </SecondaryButton>
        </ButtonsContainer>
        <SkipLink onClick={handleSkip}>
          Continue as Guest →
        </SkipLink>
      </BottomSection>
    </WalletConnectionContainer>
  );
};

export default WalletConnectionScreen;
